import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { formatNumber } from '../../../components/design-system/admin/format';
import type { FeatureKey, LimitKey, PlanAddon, PlanModule, Quality } from './plan-catalog';

/**
 * The words for a plan's parts, in the active language — the one replacement
 * for the FEATURE_LABELS / LIMIT_LABELS maps the plan pages each kept.
 *
 * Modules, limits and quality reuse the quote configurator's QUOTE.* keys (the
 * same words vendors see); features and add-ons have admin wording under
 * ADMIN_PLANS.* (QUOTE.* has no key for some of them).
 */
export function usePlanLabels() {
  const { t } = useTranslation();
  return useMemo(
    () => ({
      moduleTitle: (m: PlanModule): string =>
        m.feature ? t(`QUOTE.MODULE.${m.feature}.TITLE`) : m.id === 'depth' ? t('ADMIN_PLANS.DEPTH.TITLE') : t('QUOTE.CATALOG.TITLE'),
      moduleDesc: (m: PlanModule): string =>
        m.feature ? t(`QUOTE.MODULE.${m.feature}.DESC`) : m.id === 'depth' ? t('ADMIN_PLANS.DEPTH.DESC') : t('QUOTE.CATALOG.DESC'),
      featureLabel: (k: FeatureKey): string => t(`ADMIN_PLANS.FEATURE.${k}`),
      limitLabel: (k: LimitKey): string => t(`QUOTE.VOLUME.${k}.LABEL`),
      limitUnit: (k: LimitKey): string => t(`QUOTE.VOLUME.${k}.UNIT`),
      limitInfo: (k: LimitKey): string => t(`QUOTE.VOLUME.${k}.INFO`),
      /** A limit's value: "2,000", "Unlimited" (-1), or "—" when the plan has none. */
      limitValue: (v: number | null | undefined): string => (v === -1 ? t('ADMIN_SHELL.COMMON.UNLIMITED') : formatNumber(v)),
      addonTitle: (a: PlanAddon): string => t(`ADMIN_PLANS.ADDON.${a.id}.TITLE`),
      addonDesc: (a: PlanAddon): string => t(`ADMIN_PLANS.ADDON.${a.id}.DESC`),
      /** "Low" */
      quality: (q: Quality): string => t(`QUOTE.QUALITY.${q.toUpperCase()}`),
      /** "Low quality" */
      qualityFull: (q: Quality): string => t(`QUOTE.QUALITY_FULL.${q}`),
    }),
    [t],
  );
}

export type PlanLabels = ReturnType<typeof usePlanLabels>;
